import { useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * Voice recorder using MediaRecorder. On stop, posts to /api/voice/transcribe
 * and calls onTranscribed(text).
 *
 * Optional onRecordingChange(bool) — notifies parent when recording starts/stops.
 */
export default function VoiceRecorder({ onTranscribed, disabled, onRecordingChange }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    onRecordingChange?.(recording);
  }, [recording, onRecordingChange]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const start = async () => {
    if (disabled || busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        await upload(blob, mime);
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error("Could not access microphone");
    }
  };

  const stop = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      recorderRef.current = null;
      setRecording(false);
    }
  };

  const upload = async (blob, mime) => {
    setBusy(true);
    try {
      const fd = new FormData();
      const ext = mime.includes("webm") ? "webm" : "m4a";
      fd.append("file", blob, `voice.${ext}`);
      const res = await fetch(`${API}/voice/transcribe`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (data.text && data.text.trim()) {
        onTranscribed(data.text.trim());
        toast.success("Voice transcribed");
      } else {
        toast.error("No speech detected");
      }
    } catch (e) {
      toast.error("Transcription failed");
    } finally {
      setBusy(false);
    }
  };

  if (recording) {
    return (
      <button
        type="button"
        data-testid="voice-stop-button"
        onClick={stop}
        className="h-12 w-12 grid place-items-center bg-red-600 hover:bg-red-500 text-white transition-colors"
        title="Stop recording"
      >
        <Square size={18} fill="white" />
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid="voice-record-button"
      onClick={start}
      disabled={disabled || busy}
      className="h-12 w-12 grid place-items-center bg-[#FF5100] hover:bg-[#E64A00] text-white disabled:opacity-50 transition-colors"
      title="Record a voice note"
    >
      <Mic size={18} />
    </button>
  );
}

export function RecordingIndicator() {
  return (
    <div
      data-testid="voice-recording-indicator"
      className="flex items-center gap-3 px-4 py-3 border border-neutral-200 bg-white"
    >
      <span className="rec-dot inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
      <span className="font-mono-tech text-xs text-neutral-500 tracking-wider">REC</span>
      <div className="flex items-end h-6">
        <span className="wave-bar" />
        <span className="wave-bar" />
        <span className="wave-bar" />
        <span className="wave-bar" />
        <span className="wave-bar" />
        <span className="wave-bar" />
        <span className="wave-bar" />
      </div>
      <span className="ml-auto text-xs text-neutral-500">
        Recording... tap stop when done.
      </span>
    </div>
  );
}
